import React, { useCallback, useEffect, useMemo, useRef } from 'react';

interface StreamCuePointProps {
  text: string;
}

const StreamCuePoint: React.FC<StreamCuePointProps> = ({ text }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return () => {}; // Always return a function

    const textEl = container.querySelector<HTMLSpanElement>('.scroll-text');
    if (!textEl) return () => {};

    // Function to update animation based on text width
    const updateAnimation = () => {
      if (textEl.scrollWidth > container.offsetWidth) {
        textEl.classList.add('animate');
      } else {
        textEl.classList.remove('animate');
      }
    };

    // Initial check
    updateAnimation();

    // Create a mutation observer to watch for text changes
    const observer = new MutationObserver(() => {
      updateAnimation();
    });

    observer.observe(textEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    // Also update on window resize in case container width changes
    window.addEventListener('resize', updateAnimation);

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateAnimation);
    };
  }, [text]);

  const decodeHtmlEntities = useCallback((html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.documentElement.textContent || '';
  }, []);

  const decodedText = useMemo(
    () => decodeHtmlEntities(text),
    [text, decodeHtmlEntities]
  );

  return (
    <div className="stream-cue-point-data" ref={containerRef}>
      <span className="scroll-text">{decodedText}</span>
    </div>
  );
};

export default StreamCuePoint;
